import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import ExecutionDetailModal from '../components/ExecutionDetailModal';
import PaymentModal from '../components/PaymentModal';
import ReworkEditModal from '../components/ReworkEditModal';
import {
  Clock, CheckCircle, AlertTriangle,
  DollarSign, Eye, FileText, Download,
  Edit, RefreshCw, Lock, CreditCard, Pencil
} from 'lucide-react';

const STATUS_MAP = {
  submitted: { label: 'Submitted', color: 'bg-blue-50 text-blue-700', icon: Clock },
  under_review: { label: 'Under Review', color: 'bg-amber-50 text-amber-700', icon: Eye },
  rework_required: { label: 'Rework Required', color: 'bg-orange-50 text-orange-700', icon: AlertTriangle },
  approved: { label: 'Approved', color: 'bg-green-50 text-green-700', icon: CheckCircle },
  paid: { label: 'Paid', color: 'bg-emerald-50 text-emerald-700', icon: DollarSign },
  draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: FileText },
};

export default function ClientDashboard() {
  const [executions, setExecutions] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [reworkData, setReworkData] = useState(null);
  const navigate = useNavigate();
  const user = api.getUser();

  useEffect(() => {
    if (!user) { 
      navigate('/login'); 
      return; 
    }
    const loadData = async () => {
      setLoading(true);
      try {
        const [execData, draftData] = await Promise.all([
          api.getMyExecutions().catch(() => []),
          api.getMyDrafts().catch(() => []),
        ]);
        setExecutions(execData || []);
        setDrafts(draftData || []);
      } catch (err) {
        console.error('ClientDashboard: Error loading data:', err);
      }
      setLoading(false);
    };
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  const loadExecutions = async () => {
    setLoading(true);
    try {
      const [execData, draftData] = await Promise.all([
        api.getMyExecutions().catch(() => []),
        api.getMyDrafts().catch(() => []),
      ]);
      setExecutions(execData || []);
      setDrafts(draftData || []);
    } catch (err) {
      console.error('Error loading data:', err);
    }
    setLoading(false);
  };

  const openDetail = async (ex) => {
    setDetailLoading(true);
    try {
      const data = await api.getMyExecutionDetail(ex.id);
      setDetail(data);
    } catch (err) {
      console.error(err);
      setDetail({ execution: ex, questions: [], milestones: [], rework_requests: [] });
    }
    setDetailLoading(false);
  };

  const handleDownloadPdf = () => {
    if (!detail?.execution) return;
    const ex = detail.execution;
    if (ex.payment_status !== 'paid' && ex.status !== 'paid') {
      alert('PDF report is available after payment. Please complete payment first.');
      return;
    }
    api.downloadPdf(api.getClientPdfUrl(ex.id)).catch(err => alert(err.message));
  };

  const handlePayment = (ex) => {
    setSelectedExecution(ex);
    setShowPaymentModal(true);
  };

  const handleOpenRework = async (ex) => {
    setDetailLoading(true);
    try {
      const data = await api.getMyExecutionDetail(ex.id);
      setReworkData(data);
      setShowReworkModal(true);
    } catch (err) {
      console.error('Error loading rework data:', err);
      alert('Failed to load rework data');
    }
    setDetailLoading(false);
  };

  const handleReworkSubmitted = (result) => {
    // Reload executions to get updated status
    loadExecutions();
    setShowReworkModal(false);
    setReworkData(null);
  };

  const handleLogout = () => { api.clearAuth(); navigate('/'); };

  const statusBadge = (s) => {
    const info = STATUS_MAP[s] || STATUS_MAP.draft;
    const Icon = info.icon;
    return (
      <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-medium ${info.color}`}>
        <Icon className="w-3 h-3" /> {info.label}
      </span>
    );
  };

  if (loading) return <div className="min-h-screen bg-gray-50 flex items-center justify-center text-gray-400">Loading...</div>;

  const isPaid = (ex) => ex.payment_status === 'paid' || ex.status === 'paid';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Detail Modal - pass isPaid flag to control blur */}
      {detail && (
        <ExecutionDetailModal
          detail={detail}
          onClose={() => setDetail(null)}
          actions={[]}
          onDownloadPdf={isPaid(detail.execution || {}) ? handleDownloadPdf : null}
          isPaid={isPaid(detail.execution || {})}
          onPayment={() => handlePayment(detail.execution)}
        />
      )}

      {/* Payment Modal */}
      {showPaymentModal && selectedExecution && (
        <PaymentModal
          assessmentId={selectedExecution.assessment_id}
          executionId={selectedExecution.id}
          paymentType="single"
          onClose={() => { setShowPaymentModal(false); setSelectedExecution(null); loadExecutions(); }}
        />
      )}

      {/* Rework Edit Modal */}
      {showReworkModal && reworkData && (
        <ReworkEditModal
          execution={reworkData.execution}
          questions={reworkData.questions}
          reworkRequests={reworkData.rework_requests}
          onClose={() => { setShowReworkModal(false); setReworkData(null); }}
          onSubmitted={handleReworkSubmitted}
        />
      )}

      {detailLoading && (
        <div className="fixed inset-0 bg-black/10 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl p-6 shadow-xl text-gray-500">Loading details...</div>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900">My Assessments</h1>
          <button onClick={() => navigate('/')} data-testid="start-new-assessment-btn"
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-lg text-sm font-medium transition-colors flex items-center gap-2">
            <Edit className="w-4 h-4" /> New Assessment
          </button>
        </div>

        {/* Drafts (in-progress, not yet submitted) */}
        {drafts.length > 0 && (
          <section className="mb-8" data-testid="drafts-section">
            <h2 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
              <FileText className="w-4 h-4 text-amber-500" /> In-Progress Drafts ({drafts.length})
            </h2>
            <div className="space-y-2">
              {drafts.map(d => {
                const answered = (d.answers || []).length;
                const updatedAt = d.updated_at ? new Date(d.updated_at).toLocaleString() : '-';
                return (
                  <div key={d.id} data-testid={`draft-card-${d.id}`}
                    className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-4 flex items-center justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <p className="text-gray-900 font-semibold truncate">{d.company_info?.company_name || 'Untitled draft'}</p>
                        {d.assessment_name && (
                          <span className="text-xs bg-white border border-amber-200 text-amber-800 px-2 py-0.5 rounded font-medium" data-testid={`draft-assessment-name-${d.id}`}>
                            {d.assessment_name}
                          </span>
                        )}
                        <span className="text-xs bg-amber-100 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-medium">Draft</span>
                      </div>
                      <p className="text-xs text-gray-500">
                        ID <span className="font-mono">{(d.id || '').slice(0, 8)}</span> · {answered} answer{answered === 1 ? '' : 's'} saved · Last edited {updatedAt}
                      </p>
                    </div>
                    <button onClick={() => navigate(`/assessment/${d.assessment_id || ''}?draft=${d.id}`)}
                      data-testid={`resume-draft-${d.id}`}
                      className="inline-flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap">
                      Resume <RefreshCw className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {executions.length === 0 && drafts.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-12 text-center">
            <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500 text-lg font-medium">No assessments yet</p>
            <p className="text-gray-400 text-sm mt-1">Start your first sustainability assessment</p>
            <button onClick={() => navigate('/')}
              className="mt-4 bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-colors">
              Browse Assessments
            </button>
          </div>
        ) : executions.length === 0 ? null : (
          <div className="space-y-3">
            {executions.map(ex => (
              <div key={ex.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden hover:border-gray-300 transition-colors cursor-pointer"
                onClick={() => openDetail(ex)} data-testid={`execution-card-${ex.id}`}>
                <div className="flex items-center gap-4 px-6 py-5">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <p className="text-gray-900 font-semibold">{ex.company_name}</p>
                      {statusBadge(ex.status)}
                      <span className={`text-xs px-2 py-0.5 rounded font-medium ${ex.payment_status === 'paid' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                        {ex.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>
                    <p className="text-xs text-gray-400">
                      Submitted {ex.submitted_at ? new Date(ex.submitted_at).toLocaleDateString() : '-'}
                      {ex.contact_email && ` | ${ex.contact_email}`}
                    </p>
                    {ex.status === 'rework_required' && (
                      <p className="text-xs text-orange-600 mt-1 flex items-center gap-1">
                        <AlertTriangle className="w-3 h-3" /> Action required: Please review and update flagged questions
                      </p>
                    )}
                  </div>

                  <div className="text-right flex items-center gap-4">
                    <div>
                      <p className="text-2xl font-bold text-gray-900">{(ex.total_score || 0).toFixed(1)}%</p>
                      <p className="text-xs text-gray-400">Score</p>
                    </div>
                    {/* Edit Answers button for rework-required */}
                    {ex.status === 'rework_required' && (
                      <button onClick={(e) => { e.stopPropagation(); handleOpenRework(ex); }} data-testid={`edit-rework-${ex.id}`}
                        className="px-4 py-2 bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white rounded-lg text-sm font-medium transition-all flex items-center gap-2">
                        <Pencil className="w-4 h-4" /> Edit Answers
                      </button>
                    )}
                    {!isPaid(ex) && ex.status !== 'rework_required' && (
                      <button onClick={(e) => { e.stopPropagation(); handlePayment(ex); }} data-testid={`pay-btn-${ex.id}`}
                        className="px-4 py-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white rounded-lg text-sm font-medium transition-all flex items-center gap-2">
                        <CreditCard className="w-4 h-4" /> Unlock Full Report
                      </button>
                    )}
                    {isPaid(ex) && (
                      <button onClick={(e) => {
                        e.stopPropagation();
                        api.downloadPdf(api.getClientPdfUrl(ex.id)).catch(err => alert(err.message));
                      }} data-testid={`download-pdf-${ex.id}`}
                        className="p-2 bg-gray-900 hover:bg-gray-800 text-white rounded-lg transition-colors" title="Download PDF Report">
                        <Download className="w-4 h-4" />
                      </button>
                    )}
                    <Eye className="w-5 h-5 text-gray-400" />
                  </div>
                </div>

                {/* Category scores preview - blurred for unpaid */}
                {ex.category_scores && (
                  <div className={`grid grid-cols-4 border-t border-gray-100 ${!isPaid(ex) ? 'relative' : ''}`}>
                    {[['general', 'General'], ['environmental', 'Environmental'], ['social', 'Social'], ['governance', 'Governance']].map(([k, label]) => (
                      <div key={k} className={`px-4 py-2.5 text-center border-r border-gray-100 last:border-r-0 ${!isPaid(ex) ? 'blur-sm' : ''}`}>
                        <p className="text-xs text-gray-400">{label}</p>
                        <p className="text-sm font-bold text-gray-800">{(ex.category_scores?.[k] || 0).toFixed(1)}%</p>
                      </div>
                    ))}
                    {!isPaid(ex) && (
                      <div className="absolute inset-0 flex items-center justify-center bg-white/60">
                        <span className="flex items-center gap-1.5 text-xs font-medium text-gray-600">
                          <Lock className="w-3 h-3" /> Pay to unlock details
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
